import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PublicPageBackground from '../../components/PublicPageBackground';
import './Auth.css';
import './Legal.css';

interface LoginProps {
  setIsLoggedIn: (value: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ setIsLoggedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  // Countdown timer for resend button
  React.useEffect(() => {
    if (showVerificationScreen && resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0) {
      setCanResend(true);
    }
  }, [showVerificationScreen, resendCountdown]);

  const handleResendVerification = async () => {
    setResending(true);
    setResendSuccess(false);
    setError('');

    try {
      await api.post('/auth/resend-verification', { email: unverifiedEmail });
      setResendSuccess(true);
      setCanResend(false);
      setResendCountdown(60); // Reset countdown
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setShowVerificationScreen(false);
    setUnverifiedEmail('');
    setError('');
    setResendCountdown(60);
    setCanResend(false);
    setResendSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      // Direct API call - no service layer
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;

      // Store auth data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Update parent component state
      setIsLoggedIn(true);
      
      // Dispatch refresh feed event for when user navigates to feed
      window.dispatchEvent(new CustomEvent('refreshFeed'));
      
      navigate('/feed');
    } catch (err: any) {
      // Check if it's a network/connection error
      if (!err.response) {
        // Network error - server is not reachable
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (err.response?.status === 403 && err.response?.data?.requiresVerification) {
        // Email not verified - show verification screen
        setUnverifiedEmail(err.response.data.email || email);
        setShowVerificationScreen(true);
        setError('');
      } else {
        // Server responded with an error
        setError(err.response?.data?.error || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show verification screen if user is unverified
  if (showVerificationScreen) {
    return (
      <PublicPageBackground>
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <button onClick={handleBackToLogin} className="back-arrow" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="auth-title-section">
                <h1>Verify Your Email</h1>
              </div>
            </div>

            <div className="verification-success-container">
              <p className="verification-success-title">
                Email Verification Required
              </p>
              
              <p className="verification-success-subtitle">
                Please verify your email to continue
              </p>
              
              <p className="verification-success-email">
                {unverifiedEmail}
              </p>
              
              <p className="verification-success-instructions">
                We've sent a verification link to your email. 
                Please check your inbox and click the link to activate your account.
              </p>
              
              {resendSuccess ? (
                <p className="verification-resend-success">
                  ✓ Verification email sent again! Check your inbox.
                </p>
              ) : (
                <p className="verification-resend-hint">
                  Didn't receive the email? Check your spam folder.
                </p>
              )}
            </div>

            <div className="verification-auth-footer">
              <button
                onClick={handleResendVerification}
                disabled={!canResend || resending}
                className="auth-button"
                style={{ 
                  opacity: !canResend ? 0.5 : 1,
                  cursor: !canResend ? 'not-allowed' : 'pointer'
                }}
              >
                {resending ? 'Sending...' : canResend ? 'Resend Verification Email' : `Resend Email (${resendCountdown}s)`}
              </button>
            </div>
          </div>
        </div>
      </PublicPageBackground>
    );
  }

  return (
    <PublicPageBackground>
      <div className="auth-container">
        <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="back-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <div className="auth-title-section">
            <h1>SIGN IN</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="forgot-password-link">
            <Link to="/forgot-password" className="auth-link">
              Forgot your password?
            </Link>
          </div>

          {error && <div className="forgot-password-error">{error}</div>}

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          
        </form>

          <p className="auth-switch">
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Sign Up
            </Link>
          </p>
        
        </div>
      </div>
    </PublicPageBackground>
  );
};

export default Login;
